import React, { ForwardedRef, RefObject, useCallback, useMemo } from 'react';
import {
  DataPointsCell,
  DataPointsCellHeader,
  DataPointsCellSubHeader,
  DepartmentName,
  ProgramSummaryDialogContent,
  ProgramSummaryDialogTitle,
  ProgramSummaryLargeDialog,
  ScenarioName,
  DeptSecondaryIdentifier,
  SpaceCategoryDialogBox,
  SpaceCategoryLabel,
  useStylesProgramSummary,
  DialogTable,
} from 'spw-components/dialogs/total-program/styles';
import { InvisibleButton } from 'spw-shared/globalComponents/buttons';
import { Box, Grid, TableBody, TableHead, TableRow } from '@mui/material';
import { SmallBubbleIcon } from 'spw-components/charts/styles';
import {
  applyOverflowingRulesToDefaultPosition,
  asPercentageRounded,
  asUsf,
  getColor,
  Keys,
} from 'spw-shared/utils';
import {
  AMENITY_SPACES_CATEGORY,
  INDIVIDUAL_WORKSPACES_CATEGORY,
  MEETING_SPACES_CATEGORY,
  NO_DATA_TO_SHOW,
  SHARED_SPACES_CATEGORY,
  SUPPORT_SPACES_CATEGORY,
  TPS_CARDS_GUTTER,
} from 'spw-constants';
import { IProjectColors } from 'spw-redux/modules/projects/types';
import { IAppState } from 'spw-redux/reducer';
import { connect } from 'react-redux';
import {
  getScenarioValues,
  PaperComponent,
  SPACE_TYPE_ALLOCATION,
} from 'spw-components/dialogs/total-program/utils';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import { ISpaceChartData } from 'spw-components/charts/small-donut-chart-detail';
import { PlainWorksheet } from '../../../models/Worksheet';

interface SpaceTypeAllocationProps {
  projectColors: IProjectColors;
  showDialog: boolean;
  setShowDialog: (show: boolean) => void;
  totalProgramWorksheets: PlainWorksheet[];
  usf: number | string;
  usfPercent: number | string;
  valueType: string;
}

const spaceTitle = {
  [INDIVIDUAL_WORKSPACES_CATEGORY]: 'Individual Workspaces',
  [MEETING_SPACES_CATEGORY]: 'Meeting Spaces',
  [SUPPORT_SPACES_CATEGORY]: 'Support Spaces',
  [AMENITY_SPACES_CATEGORY]: 'Amenity Spaces',
  [SHARED_SPACES_CATEGORY]: 'Shared Spaces',
} as { [key: string]: number | string };

interface ISpaceTypeAllocationDialogProps extends SpaceTypeAllocationProps {
  showProgramSubtotalUsf: () => void;
  openDialog: (
    selectedValueType: string,
    usfValue: number | string,
    usfPercentValue: number | string
  ) => void;
  spacesData: ISpaceChartData[];
  anchorRef: ForwardedRef<HTMLElement>;
  isDetailedChart: boolean;
}

const getTopLeft = (element: HTMLElement, isDetailedChart: boolean) => {
  return applyOverflowingRulesToDefaultPosition((rect) => {
    const horizontalOffset = isDetailedChart
      ? rect.width + TPS_CARDS_GUTTER
      : (rect.width + TPS_CARDS_GUTTER) * 2;

    return {
      x: rect.left + horizontalOffset,
      y: rect.top - 249,
    };
  }, element);
};

const SpaceTypeAllocationDialog: React.FC<ISpaceTypeAllocationDialogProps> = ({
  projectColors,
  showDialog,
  setShowDialog,
  totalProgramWorksheets,
  usf,
  usfPercent,
  valueType,
  showProgramSubtotalUsf,
  openDialog,
  spacesData,
  anchorRef,
  isDetailedChart,
}) => {
  const { getUnit, convertArea } = useUnitConversion();
  const classes = useStylesProgramSummary({});

  const openSpaceCategoryDialog = (category: string) => {
    const spaceData = spacesData.find(
      (data: ISpaceChartData) => data.category === category
    );

    const spaceCategoryUsf = spaceData.quantity
      ? asUsf(spaceData.quantity)
      : NO_DATA_TO_SHOW;

    const spaceCategoryUsfPercent = asPercentageRounded(
      spaceData.percent,
      true
    );

    openDialog(category, spaceCategoryUsf, spaceCategoryUsfPercent);
  };

  const selectedCategoryData = useMemo(() => {
    return spacesData.find(
      (data: ISpaceChartData) => data.category === valueType
    );
  }, [valueType, spacesData]);

  const memoizedGetTopLeft = useCallback(
    (element: HTMLElement) => getTopLeft(element, isDetailedChart),
    [isDetailedChart]
  );

  return (
    <ProgramSummaryLargeDialog
      open={showDialog}
      disablePortal
      disableEnforceFocus
      hideBackdrop
      disableScrollLock
      PaperProps={{
        handle: '#space-type-allocation-dialog',
        component: PaperComponent,
      }}
      anchorRef={anchorRef as RefObject<HTMLElement>}
      getTopLeft={memoizedGetTopLeft}
    >
      <ProgramSummaryDialogTitle id="space-type-allocation-dialog">
        <Box display="flex">
          <Box alignItems="center" display="flex">
            Space Type Allocation
          </Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            flexGrow="1"
          >
            <InvisibleButton
              className={classes.closeButton}
              onClick={() => {
                setShowDialog(false);
              }}
            >
              CLOSE
            </InvisibleButton>
          </Box>
        </Box>
      </ProgramSummaryDialogTitle>
      <ProgramSummaryDialogContent>
        <Grid container>
          <Grid item xs={12}>
            <SpaceCategoryDialogBox
              className={
                valueType === SPACE_TYPE_ALLOCATION.PROGRAM_SUBTOTAL_USF
                  ? 'selected'
                  : 'selectable'
              }
              style={{ marginBottom: 8, justifyContent: 'center' }}
              onClick={showProgramSubtotalUsf}
            >
              Subtotal Program {getUnit('area')}
            </SpaceCategoryDialogBox>
          </Grid>
        </Grid>

        <Grid container spacing={2} rowSpacing={1}>
          {spacesData.map((spaceData: ISpaceChartData) => {
            return (
              <Grid item xs={6} key={`space-box-${spaceData.category}`}>
                <SpaceCategoryDialogBox
                  className={
                    valueType === spaceData.category ? 'selected' : 'selectable'
                  }
                  onClick={() => {
                    openSpaceCategoryDialog(spaceData.category);
                  }}
                >
                  <SmallBubbleIcon
                    color={getColor(spaceData.category as Keys, projectColors)}
                  />
                  <SpaceCategoryLabel>
                    {spaceData.name || spaceTitle[spaceData.category]}
                  </SpaceCategoryLabel>
                </SpaceCategoryDialogBox>
              </Grid>
            );
          })}
        </Grid>
        <DialogTable>
          <TableHead>
            <TableRow className="scenario-name-row">
              <DataPointsCellHeader width="60%">
                Scenario Name
              </DataPointsCellHeader>
            </TableRow>
            <TableRow className="department-name-row">
              <DataPointsCellHeader width="70%">
                Department Name
              </DataPointsCellHeader>
              <DataPointsCellHeader align="right" width="15%">
                {getUnit('area')}
              </DataPointsCellHeader>
              <DataPointsCellHeader align="right" width="15%">
                % {getUnit('area')}
              </DataPointsCellHeader>
            </TableRow>
            <TableRow>
              <DataPointsCellSubHeader>
                {valueType === SPACE_TYPE_ALLOCATION.PROGRAM_SUBTOTAL_USF ? (
                  'Subtotal Program USF'
                ) : (
                  <Box display="flex" alignItems="center">
                    <SmallBubbleIcon
                      color={getColor(valueType as Keys, projectColors)}
                    />
                    <span style={{ marginLeft: 8 }}>
                      {selectedCategoryData.name || spaceTitle[valueType]}
                    </span>
                  </Box>
                )}
              </DataPointsCellSubHeader>
              <DataPointsCellSubHeader align="right">
                {usf}
              </DataPointsCellSubHeader>
              <DataPointsCellSubHeader align="right">
                {usfPercent}
              </DataPointsCellSubHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {totalProgramWorksheets.map((worksheet) => {
              const { scenario } = worksheet;
              const { department, name } = scenario;
              const { totalUsf, usfPercent: percent } = getScenarioValues(
                worksheet,
                valueType
              );
              return (
                <TableRow key={`row-${scenario.id}`}>
                  <DataPointsCell>
                    <ScenarioName>{name}</ScenarioName>
                    <DepartmentName>{department.name}</DepartmentName>
                    <DeptSecondaryIdentifier>
                      {department.secondaryIdentifier}
                    </DeptSecondaryIdentifier>
                  </DataPointsCell>
                  <DataPointsCell align="right">
                    {asUsf(convertArea(parseFloat(totalUsf.replace(/,/g, ''))))}
                  </DataPointsCell>
                  <DataPointsCell align="right">{percent}</DataPointsCell>
                </TableRow>
              );
            })}
          </TableBody>
        </DialogTable>
      </ProgramSummaryDialogContent>
    </ProgramSummaryLargeDialog>
  );
};

const mapStateToProps = (state: IAppState) => ({
  projectColors: state.projectsReducer.colors,
});

export default connect(mapStateToProps)(SpaceTypeAllocationDialog);
